/**
 * V2 header auth — who you are, and the one way in or out.
 *
 * "Sign in" opens a native <dialog> (focus trap + Escape for free) with
 * Sign in / Create account tabs: Google OAuth, or email + password —
 * including full email sign-UP with username pre-check (V1 parity,
 * restored M8.5).
 * Familiarity rules: plain words, native buttons and labeled inputs, no
 * theme-speak. The signed-in name is the only handwritten element.
 * Renders nothing until auth state is known so the header never flashes
 * the wrong state.
 */

/**
 * Dispatch this on `document` to open the sign-in dialog from anywhere:
 *
 *     document.dispatchEvent(new CustomEvent(OPEN_AUTH_EVENT));
 *
 * No-ops when signed in or already open, so a caller never has to check.
 * detail: { mode: 'signup' } opens on the Create-account tab instead.
 */
export const OPEN_AUTH_EVENT = 'yoink:open-auth';
import { signInWithGoogle, signInWithEmail, signUpWithEmail, signOutUser, requestPasswordReset, mountGoogleButton } from '../core/auth.js';
import { escapeHtml, isValidEmail } from '../../assets/js/utils.js';
import { sourceFieldHtml, wireSourceField, readSourceField, markAskedPending } from '../core/signup-source.js';

/**
 * The name they chose to show, not the unique handle (Dru Morgan, not
 * drumorgan) — and never the email address.
 *
 * The email used to sit third in this chain, so an account with no
 * full_name showed the local part of its address in the site header:
 * "murano.yuki" on an account that reads "muranoyuki". Every email signup
 * has a null full_name (the Create-account form collects a username but no
 * name, and the profile trigger seeds full_name from OAuth metadata only),
 * so that was the normal case for anyone who did not arrive via Google —
 * not an edge case. The @username is the fallback: a public handle they
 * picked, already on their profile URL, and not a credential here (sign-in
 * is by email address).
 */
function displayName(profile) {
  return profile?.full_name?.trim() || profile?.username?.trim() || 'Explorer';
}

// Two tabs like V1's auth modal (yoink-page.php:1247-1305): Sign in and
// Create account — email/password signup is a first-class way in, not
// just Google (M8.5 Tier 1).
function dialogHtml(mode = 'signin') {
  const signin = mode === 'signin';
  return `
  <h2 class="v2-dialog-title">${signin ? 'Sign in' : 'Create account'}</h2>
  <div class="v2-scope auth-tabs" role="group" aria-label="Sign in or create account">
    <button type="button" class="v2-scope-btn ${signin ? 'scope-active' : ''}"
      data-auth-action="tab-signin" aria-pressed="${signin}">Sign in</button>
    <button type="button" class="v2-scope-btn ${signin ? '' : 'scope-active'}"
      data-auth-action="tab-signup" aria-pressed="${!signin}">Create account</button>
  </div>
  <div class="v2-gsi-slot" data-gsi-slot hidden></div>
  <button type="button" class="v2-btn-google" data-auth-action="google">Continue with Google</button>
  <p class="v2-auth-hint" data-auth-hint hidden>If Google gets stuck on “Verifying it’s you”,
    open yoinkadventures.com in your browser instead, sign in there, and come
    back — the app picks the sign-in up.</p>
  <div class="v2-dialog-divider" aria-hidden="true">or</div>
  ${signin ? `
  <form data-auth-form novalidate>
    <label class="v2-field">Email
      <input type="email" name="email" autocomplete="email" required>
    </label>
    <label class="v2-field">Password
      <input type="password" name="password" autocomplete="current-password" required>
    </label>
    <div class="v2-form-error" data-auth-form-error role="alert"></div>
    <button type="submit" class="v2-btn-email">Sign in with email</button>
  </form>
  <button type="button" class="v2-btn-dialog-close" data-auth-action="forgot">Forgot password?</button>` : `
  <form data-signup-form novalidate>
    <label class="v2-field">Username
      <input type="text" name="username" autocomplete="username" maxlength="30" required>
    </label>
    <label class="v2-field">Email
      <input type="email" name="email" autocomplete="email" required>
    </label>
    <label class="v2-field">Password
      <input type="password" name="password" autocomplete="new-password" minlength="6" required>
    </label>
    ${sourceFieldHtml()}
    <div class="v2-form-error" data-auth-form-error role="alert"></div>
    <div class="log-status" data-signup-status aria-live="polite"></div>
    <button type="submit" class="v2-btn-email">Create account</button>
  </form>`}
  <button type="button" class="v2-btn-dialog-close" data-auth-action="close">Cancel</button>
`;
}

const RESET_HTML = `
  <h2 class="v2-dialog-title">Reset password</h2>
  <form data-reset-form novalidate>
    <label class="v2-field">Email
      <input type="email" name="email" autocomplete="email" required>
    </label>
    <div class="v2-form-error" data-auth-form-error role="alert"></div>
    <div class="log-status" data-reset-status aria-live="polite"></div>
    <button type="submit" class="v2-btn-email">Send reset link</button>
  </form>
  <button type="button" class="v2-btn-dialog-close" data-auth-action="close">Cancel</button>
`;

/**
 * Mount the auth controls.
 * @param {HTMLElement} el - Header slot to render into
 * @param {object} ctx - { store }
 * @returns {() => void} cleanup
 */
export function mount(el, ctx) {
  const { store } = ctx;

  function render(state) {
    const { authReady, user, profile, profileLoading } = state;
    if (!authReady) {
      el.innerHTML = '';
      return;
    }
    // A store change while the dialog is open is a sign-in landing —
    // innerHTML replacement closes it, which is the right outcome.
    if (user) {
      // authReady flips as soon as the session is restored, but the profile
      // is a separate fetch that is still in flight at that moment — so the
      // header used to paint the email handle first and swap to the real
      // name a beat later. Someone photographed the beat: "murano.yuki"
      // where his account reads "muranoyuki". Hold the name back until the
      // profile resolves; the email is a last resort, not a placeholder.
      const name = profileLoading && !profile ? '' : escapeHtml(displayName(profile));
      el.innerHTML = `
        ${name ? `<span class="v2-auth-name">${name}</span>` : ''}
        <button type="button" class="v2-btn-auth" data-auth-action="signout">Sign out</button>`;
    } else {
      el.innerHTML = `
        <button type="button" class="v2-btn-auth v2-btn-signin" data-auth-action="open">Sign in</button>
        <dialog class="v2-auth-dialog">${dialogHtml()}</dialog>`;
    }
  }

  function setFormError(message) {
    const errorEl = el.querySelector('[data-auth-form-error]');
    if (errorEl) errorEl.textContent = message;
  }

  async function onGoogle(button) {
    button.disabled = true;
    button.textContent = 'Opening Google…';
    try {
      await signInWithGoogle(); // navigates away on success
    } catch (err) {
      setFormError(err.message || 'Google sign-in failed. Please try again.');
      button.disabled = false;
      button.textContent = 'Continue with Google';
    }
  }

  async function onEmailSubmit(form) {
    const email = form.elements.email.value.trim();
    const password = form.elements.password.value;
    if (!email || !isValidEmail(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setFormError('Please enter your password.');
      return;
    }
    setFormError('');
    const submit = form.querySelector('.v2-btn-email');
    submit.disabled = true;
    submit.textContent = 'Signing in…';
    try {
      await signInWithEmail(store, email, password);
      // applySession re-renders via the store, removing the dialog
    } catch (err) {
      setFormError(err.message);
      submit.disabled = false;
      submit.textContent = 'Sign in with email';
    }
  }

  // In-page Google sign-in (GIS): render Google's button into the slot and
  // hide the redirect fallback. Falls back silently (no client ID, iOS,
  // script blocked) — the redirect button stays, with the stuck-passkey
  // hint added when the app runs installed (the context where the redirect
  // flow's Custom Tab freezes — docs/AUTH-AUDIT-2026-08-21.md §1). Called
  // on every dialog open/tab switch since those rebuild the dialog's DOM.
  async function setupGoogleSignIn() {
    const slot = el.querySelector('[data-gsi-slot]');
    if (!slot || slot.childElementCount > 0) return;
    const ok = await mountGoogleButton(slot, store, { onError: setFormError });
    if (!slot.isConnected) return; // dialog re-rendered while GIS loaded
    if (ok) {
      slot.hidden = false;
      const fallback = el.querySelector('[data-auth-action="google"]');
      if (fallback) fallback.hidden = true;
    } else if (window.matchMedia?.('(display-mode: standalone)').matches) {
      const hint = el.querySelector('[data-auth-hint]');
      if (hint) hint.hidden = false;
    }
  }

  function onClick(event) {
    const action = event.target.closest('[data-auth-action]')?.dataset.authAction;
    if (!action) return;
    const dialog = el.querySelector('.v2-auth-dialog');
    if (action === 'open' && dialog) {
      setFormError('');
      dialog.showModal();
      setupGoogleSignIn();
    } else if (action === 'tab-signin' && dialog) {
      dialog.innerHTML = dialogHtml('signin');
      setupGoogleSignIn();
    } else if (action === 'tab-signup' && dialog) {
      dialog.innerHTML = dialogHtml('signup');
      wireSourceField(dialog.querySelector('[data-signup-form]'));
      setupGoogleSignIn();
    } else if (action === 'forgot' && dialog) {
      dialog.innerHTML = RESET_HTML;
    } else if (action === 'close' && dialog) {
      dialog.close();
    } else if (action === 'google') {
      onGoogle(event.target.closest('[data-auth-action]'));
    } else if (action === 'signout') {
      signOutUser(store); // store update triggers re-render
    }
  }

  async function onResetSubmit(form) {
    const email = form.elements.email.value.trim();
    if (!email || !isValidEmail(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setFormError('');
    const statusEl = el.querySelector('[data-reset-status]');
    try {
      await requestPasswordReset(email);
      if (statusEl) statusEl.textContent = 'Check your email for the reset link.';
    } catch (err) {
      setFormError(err.message || 'Couldn’t send the reset email.');
    }
  }

  async function onSignUpSubmit(form) {
    const username = form.elements.username.value.trim();
    const email = form.elements.email.value.trim();
    const password = form.elements.password.value;
    if (!username) return setFormError('Please enter a username');
    if (!email || !isValidEmail(email)) return setFormError('Please enter a valid email address.');
    setFormError('');
    const source = readSourceField(form);
    const submit = form.querySelector('.v2-btn-email');
    submit.disabled = true;
    submit.textContent = 'Creating account…';
    try {
      // The question was on this form, answered or not — the post-sign-in
      // prompt must not ask it again on this device.
      markAskedPending();
      const { confirmationRequired } = await signUpWithEmail(store, email, password, username, source);
      if (confirmationRequired) {
        // No session yet — the dialog stays up with the instruction
        const status = el.querySelector('[data-signup-status]');
        if (status) status.textContent = 'Please check your email to confirm your account.';
        submit.textContent = 'Create account';
      }
      // Otherwise applySession re-renders via the store, removing the dialog
    } catch (err) {
      setFormError(err.message);
      submit.disabled = false;
      submit.textContent = 'Create account';
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    const reset = event.target.closest('[data-reset-form]');
    if (reset) {
      onResetSubmit(reset);
      return;
    }
    const signup = event.target.closest('[data-signup-form]');
    if (signup) {
      onSignUpSubmit(signup);
      return;
    }
    const form = event.target.closest('[data-auth-form]');
    if (form) onEmailSubmit(form);
  }

  // Let any view ask for the sign-in dialog without importing the header.
  // Added 2026-07-26 for the signed-out category page, which needs a real
  // call to action rather than the old "Sign in (top right)" — advice that is
  // also wrong on a phone, where the auth controls wrap onto their own row
  // (base.css: flex-wrap on .v2-header).
  //
  // A document event rather than an export because the header owns the dialog
  // and mounts independently of whatever view is on screen; a direct import
  // would couple every view to the header's lifecycle and break the moment a
  // view rendered before the header did.
  // detail.mode 'signup' opens on the Create-account tab — the visitor
  // ribbon's "Join free" would land on the wrong form otherwise.
  function onOpenRequest(event) {
    const dialog = el.querySelector('.v2-auth-dialog');
    if (!dialog || dialog.open) return; // signed in, or already showing
    if (event.detail?.mode === 'signup') {
      dialog.innerHTML = dialogHtml('signup');
      wireSourceField(dialog.querySelector('[data-signup-form]'));
    }
    setFormError('');
    dialog.showModal();
    setupGoogleSignIn();
  }

  el.addEventListener('click', onClick);
  el.addEventListener('submit', onSubmit);
  document.addEventListener(OPEN_AUTH_EVENT, onOpenRequest);
  const unsubscribe = store.subscribe(render);
  render(store.get());

  return () => {
    el.removeEventListener('click', onClick);
    el.removeEventListener('submit', onSubmit);
    document.removeEventListener(OPEN_AUTH_EVENT, onOpenRequest);
    unsubscribe();
  };
}
